import { useEffect, useRef } from 'react';
import { maxOf, sumOf } from '../../../shared/utils/arrayUtils.ts';
import { useResizeObserver } from '../../hooks/useResizeObserver.ts';
import { Operation } from '../../types/sharedTypeImpl.ts';
import { LIVE_GRAPH_MED } from '../../utils/liveGraphColors.ts';
import { trpc } from '../../utils/trpc/trpc.ts';
import { BarGraphData, BarGroup } from './barGraphData.ts';
import { drawReportGraph } from './drawReportGraph.ts';
import { ReportGraphContainer } from './reportGraphContainer.tsx';
import { PeriodOption, ReportSettings } from './report-settings/reportSettings.ts';


interface Props {
    style?: React.CSSProperties;
    reportSettings: ReportSettings;
    operatorFooterLabelsByPeriod?: string[][];
    onOperatorFooterClick?: (periodIndex: number) => void;
}

export function ScrapsTimeGraph({
    style,
    reportSettings,
    operatorFooterLabelsByPeriod,
    onOperatorFooterClick,
}: Props) {
    const graphContainerRef = useRef<HTMLDivElement>(null);
    const dimensions = useResizeObserver(graphContainerRef);


    const { data: scrapCloseOperations, isFetching } = trpc.operations.get.useQuery({
        hasScrapCloseForm: true,
        scrapCloseTime: { start: reportSettings.first.start, end: reportSettings.last.end, },
    }, { refetchOnWindowFocus: false, });

    useEffect(() => {
        if (graphContainerRef.current == null || scrapCloseOperations == null) {
            return;
        }

        const data = transformData(scrapCloseOperations, reportSettings.periodsInRange);
        drawReportGraph(graphContainerRef.current!, data, operatorFooterLabelsByPeriod, onOperatorFooterClick);
    }, [graphContainerRef, scrapCloseOperations, reportSettings, operatorFooterLabelsByPeriod, onOperatorFooterClick, dimensions]);


    function transformData(operations: Operation[], periods: PeriodOption[]): BarGraphData {
        const dataInitial = periods.map(period => {
            const operationsWeek = operations
                .filter(it => it.scrapCloseForm?.time != null && it.scrapCloseForm.timeSpent != null)
                .filter(it => it.scrapCloseForm!.time! >= period.start && it.scrapCloseForm!.time! < period.end);

            const timeSpentTotal = sumOf(operationsWeek, it => it.scrapCloseForm!.timeSpent! ?? 0) / 3600_000;
            return {
                value: timeSpentTotal,
                color: LIVE_GRAPH_MED,
                periodLabel: period.periodLabel,
                valueLabel: timeSpentTotal.toFixed(0) + 'h',
            };
        });

        const barGroups: BarGroup[] = dataInitial.map(data => ({
            periodLabel: data.periodLabel,
            bars: [{
                sections: [{
                    value: data.value,
                    color: data.color,
                    labels: [{ position: 'below-top', text: data.valueLabel }],
                }],
            }],
        }));

        const maxValue = maxOf(dataInitial, it => it.value) ?? 0;

        return {
            barGroups,
            maxValue,
        };
    }


    return (
        <ReportGraphContainer
            style={{ ...style, height: '100%' }}
            containerRef={graphContainerRef}
            isLoading={isFetching}
        />
    );
}
